<template>
    <div>
        <div
            class="border-b border-theme-border rounded-lg px-1 flex flex-wrap"
        >
            <div
                class="lg:w-2/12 md:w-1/4 w-4/12 flex justify-center items-center py-3"
            >
                <button
                    v-if="'images' in reference"
                    class="w-28 h-28 p-0"
                    @click="goToMainPage(reference.product.slug)"
                >
                    <img
                        v-img:group
                        v-img-loading
                        class="w-full h-full"
                        :src="reference.images[0].url | image"
                        :alt="reference.images[0].alt"
                        loading="lazy"
                    />
                </button>
                <img
                    v-else
                    v-img:group
                    class="w-28 h-28 p-0"
                    :src="$data.$defaultImageUrl"
                    loading="lazy"
                />
            </div>
            <div
                class="lg:w-10/12 md:w-3/4 w-8/12 flex flex-col justify-around py-3 px-1"
            >
                <div
                    class="flex lg:justify-between lg:flex-row flex-col items-center my-2"
                >
                    <div v-if="isRealProduct" class="flex flex-col w-full">
                        <router-link
                            v-if="$ability.hasPermission('inventory:Get')"
                            :to="
                                merchant
                                    ? `/app/inventories/${merchant.merchantType}/${merchant.merchantId}/detail/${reference.id}`
                                    : `/app/inventories/detail/${reference.id}`
                            "
                            class="btn-a p-0"
                        >
                            <strong>
                                {{ reference.product.name | cut(50) }}&nbsp;
                                {{
                                    reference.active === undefined
                                        ? ""
                                        : reference.active
                                        ? "🟢"
                                        : "⚪"
                                }}
                            </strong>
                        </router-link>
                        <strong v-else>
                            {{ reference.product.name | cut(50) }}&nbsp;
                            {{
                                reference.active === undefined
                                    ? ""
                                    : reference.active
                                    ? "🟢"
                                    : "⚪"
                            }}
                        </strong>
                        <div>
                            <small>
                                Prod.
                                <router-link
                                    :to="`/app/products/detail/${reference.productId}`"
                                    class="btn-a p-0"
                                >
                                    {{
                                        reference.product.sku
                                            ? `SKU ${reference.product.sku}`
                                            : `ID ${reference.productId}`
                                    }}
                                </router-link>

                                | Ref.
                                <router-link
                                    :to="`/app/products/detail/${reference.productId}?reference=${reference.id}&tab=references`"
                                    class="btn-a p-0"
                                >
                                    {{
                                        reference.barcode
                                            ? `Cód. ${reference.barcode}`
                                            : `ID ${reference.id}`
                                    }}
                                </router-link>
                            </small>
                        </div>
                    </div>
                    <div v-else class="flex flex-col w-full">
                        <span class="p-0">
                            <template v-if="isEditingName">
                                <input
                                    ref="productNameInput"
                                    v-model="editedProductName"
                                    class="form-control form-control-sm text-theme-base"
                                    autofocus
                                    @blur="saveProductName"
                                    @keyup.enter="saveProductName"
                                />
                            </template>
                            <template v-else>
                                <span
                                    style="cursor: pointer"
                                    @click="editProductName"
                                >
                                    {{ reference.product.name }}&nbsp;
                                </span>
                            </template>
                        </span>

                        <small> Servicio / P.A. </small>
                    </div>
                    <div
                        v-if="enabled"
                        id="actions"
                        class="flex w-full justify-end mt-2 items-center"
                    >
                        <custom-tooltip
                            v-if="enabledDiscount && !enabledSalePrice"
                            border
                        >
                            <custom-checkbox
                                v-model="availableDiscount"
                                warn
                                @input="changeAvailableDiscount"
                            />
                            <template #tooltip> Aplicar Descuento 🤑</template>
                        </custom-tooltip>

                        <quantity-button
                            v-model="quantity"
                            class="mx-2"
                            max="100"
                            min="1"
                            inline
                            @input="changeQuantity"
                        />

                        <app-button danger circle icon @click="remove">
                            🗑
                        </app-button>
                    </div>
                </div>

                <div
                    class="flex flex-col lg:flex-row flex-wrap lg:justify-between"
                >
                    <div
                        v-if="reference.color"
                        class="flex lg:flex-col justify-between pr-2"
                    >
                        <strong>Color </strong>
                        <div class="flex items-center">
                            <small class="capitalize">
                                {{ reference.color }}
                            </small>
                            <div
                                class="ml-1 w-5 h-5 rounded-full border border-theme-border"
                                :style="{
                                    'background-color': reference.colorRef
                                }"
                            ></div>
                        </div>
                    </div>
                    <div
                        v-if="reference.size"
                        class="flex lg:flex-col justify-between pr-2"
                    >
                        <strong>Tamaño </strong>
                        <small class="capitalize">
                            {{ reference.size }}
                        </small>
                    </div>
                    <div
                        v-if="reference.customReferenceValue"
                        class="flex lg:flex-col justify-between pr-2"
                    >
                        <strong>{{
                            reference.product &&
                            reference.product.customReferenceTitle
                                ? reference.product.customReferenceTitle
                                : "Variante"
                        }}</strong>
                        <small class="capitalize">
                            {{ reference.customReferenceValue }}
                        </small>
                    </div>
                    <div
                        v-if="
                            Number.isInteger(reference.stock) &&
                            $ability.hasPermission('inventory:Get')
                        "
                        class="flex lg:flex-col justify-between pr-2"
                        :class="{
                            'font-bold text-danger': reference.stock < quantity,
                            'line-through': reference.stock === 0
                        }"
                    >
                        <strong>Stock </strong>
                        <small>
                            {{ reference.stock }}
                        </small>
                    </div>
                    <div class="flex lg:flex-col justify-between pr-2">
                        <strong>Cantidad </strong>
                        <small
                            :class="{
                                'line-through font-bold': quantity === 0
                            }"
                        >
                            {{ quantity }}
                        </small>
                    </div>
                    <div class="flex lg:flex-col justify-between pr-2">
                        <strong>$ Unidad </strong>
                        <div class="flex">
                            <small v-if="hasSalePriceChange">
                                {{ lineSalePrice | money }}
                            </small>
                            <small
                                :class="{
                                    'line-through ml-2': hasSalePriceChange
                                }"
                            >
                                {{ reference.priceOffer | money }}
                            </small>
                        </div>
                    </div>
                    <div
                        v-if="
                            !enabledSalePrice &&
                            Number.isInteger(
                                reference.appliedDiscountPerReference
                            )
                        "
                        class="flex lg:flex-col justify-between pr-2"
                    >
                        <strong>$ Descuento u. </strong>
                        <small>
                            {{ reference.appliedDiscountPerReference | money }}
                        </small>
                    </div>
                    <div
                        v-if="
                            reference.cost && Number.isInteger(reference.cost)
                        "
                        class="flex lg:flex-col justify-between pr-2"
                    >
                        <strong>Costo unidad </strong>
                        <small>
                            {{ reference.cost | money }}
                        </small>
                    </div>
                    <div class="w-full mt-2 flex pr-2">
                        <span class="text-theme-primary font-bold">
                            $ Variantes: &nbsp;
                        </span>
                        <span
                            class="font-bold h5"
                            :class="{
                                'line-through ': quantity === 0
                            }"
                        >
                            {{ (lineSalePrice * quantity) | money }}
                        </span>
                    </div>
                </div>

                <div v-if="enabledSalePrice && enabled" class="mt-4 pr-2">
                    <input-money-component
                        v-model="salePrice"
                        enabled
                        label="Precio de venta"
                        icon="💲"
                        :message-error="salePriceError"
                        @blur="blurSalePrice"
                        @focus="focusInput"
                    />
                </div>

                <div v-else-if="availableDiscount && enabled" class="mt-4 pr-2">
                    <input-money-component
                        v-model="appliedDiscountPerReference"
                        enabled
                        label="Descuento neto por referencia"
                        icon="💲"
                        :message-error="
                            appliedDiscountPerReference >
                            reference.priceOffer * 0.6
                                ? 'Descuento no debe exceder el 60% del valor de la referencia'
                                : ''
                        "
                        @blur="blurAppliedDiscountPerReference"
                        @focus="focusInput"
                    />
                </div>
            </div>
        </div>
        <app-dialog v-model="isOpenModalGetInventory">
            <template #header>
                <div class="flex flex-col mx-3 my-4">
                    <span class="text-lg">
                        Inventario para referencia
                        <span class="text-theme-primary">
                            # {{ reference.id }}
                        </span>
                    </span>
                    <small>
                        {{
                            [reference.color, reference.size]
                                .filter(Boolean)
                                .join(" | ")
                        }}
                        <template v-if="reference.customReferenceValue">
                            |
                            {{
                                reference.product &&
                                reference.product.customReferenceTitle
                                    ? `${reference.product.customReferenceTitle}: `
                                    : ""
                            }}{{ reference.customReferenceValue }}
                        </template>
                    </small>
                </div>
            </template>
            <div>
                <inventory-by-reference-table
                    :inventories="inventories"
                    :stock-active="stockActive"
                />
            </div>
        </app-dialog>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import { DEFAULT_IMAGE_URL } from "@/common/constants";
import {
    resolveLineSalePrice,
    validateLineSalePrice
} from "@/common/lib/order-line-price";

import InventoryByReferenceTable from "@/components/inventories/InventoryByReferenceTable.vue";
import AppDialog from "@/components/utils/AppDialog.vue";
import InputMoneyComponent from "@/components/utils/InputMoneyComponent.vue";
import QuantityButton from "@/components/utils/QuantityButton.vue";

export default {
    name: "ReferenceTableItem",
    components: {
        AppDialog,
        InputMoneyComponent,
        QuantityButton,
        InventoryByReferenceTable
    },
    props: {
        reference: {
            type: Object,
            required: true,
            default: () => ({
                id: null,
                anonymousId: null,
                size: "pequeño",
                color: "rojo",
                colorRef: "#f00",
                quantity: 1,
                price: 10000,
                priceOffer: 10000,
                appliedDiscountPerReference: 0,
                images: [
                    {
                        id: 1,
                        url: DEFAULT_IMAGE_URL,
                        alt: "alt"
                    }
                ],
                product: {
                    id: "",
                    name: "",
                    summary: ""
                }
            })
        },
        merchant: {
            default: () => null,
            type: Object, //{merchantId:string,merchantType:string}
            required: false
        },
        enabled: {
            default: () => false,
            type: Boolean,
            required: false
        },
        enabledDiscount: {
            type: Boolean,
            required: false,
            default: () => false
        },
        // pedido-domicilio-portal: "Precio de venta" por unidad en lugar del
        // descuento.
        enabledSalePrice: {
            type: Boolean,
            required: false,
            default: () => false
        },
        allowAboveCatalog: {
            type: Boolean,
            required: false,
            default: () => false
        }
    },
    emits: ["update", "remove", "focus"],
    data: () => ({
        isEditingName: false,
        editedProductName: "",
        quantity: 0,
        appliedDiscountPerReference: 0,
        salePrice: 0,
        inventories: [],
        isOpenModalGetInventory: false,
        availableDiscount: false,
        isLoadingGetInventory: false,
        stockActive: 0,
        $defaultImageUrl: DEFAULT_IMAGE_URL
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"]),
        lineSalePrice() {
            return resolveLineSalePrice(this.reference);
        },
        hasSalePriceChange() {
            return (
                this.enabledSalePrice &&
                this.lineSalePrice !== Number(this.reference.priceOffer)
            );
        },
        salePriceError() {
            return validateLineSalePrice({
                salePrice: this.salePrice,
                priceOffer: this.reference.priceOffer,
                allowAboveCatalog: this.allowAboveCatalog
            });
        },
        isRealProduct() {
            return !!this.reference.id && !!this.reference.product.id;
        }
    },
    watch: {
        reference(value) {
            this.setInput(value);
        }
    },
    mounted() {
        this.setInput(this.reference);
    },
    methods: {
        ...mapActions("inventories", ["listInventoryByReference"]),
        async listInventoryReferenceComponent() {
            this.isLoading = true;
            this.isOpenModalGetInventory = true;
            this.isLoadingGetInventory = true;
            try {
                const { items, stockActive } =
                    await this.listInventoryByReference({
                        id: this.reference.id
                    });
                this.inventories = items;
                this.stockActive = stockActive;
                this.isLoading = false;
                this.isLoadingGetInventory = false;
            } catch (error) {
                this.isLoading = false;
                this.isLoadingGetInventory = false;
                this.isOpenModalGetInventory = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        goToMainPage(slug) {
            if (this.reference.active) {
                window.open(`${this.$publicUrl}/${slug}`, "_blank").focus();
            }
        },
        remove() {
            this.$emit("remove", {
                id: this.reference.id,
                anonymousId: this.reference.anonymousId
            });
        },
        blurAppliedDiscountPerReference() {
            this.$emit("update", {
                ...this.reference,
                quantity: this.quantity,
                appliedDiscountPerReference: this.appliedDiscountPerReference
            });
        },
        blurSalePrice() {
            this.$emit("update", {
                ...this.reference,
                quantity: this.quantity,
                salePrice: this.salePrice
            });
        },
        focusInput() {
            this.$emit("focus");
        },
        setInput(value) {
            this.quantity = Number(value.quantity);
            this.salePrice = resolveLineSalePrice(value);
            this.appliedDiscountPerReference =
                Number(value.appliedDiscountPerReference) || 0;
            this.availableDiscount = this.appliedDiscountPerReference > 0;
            this.editedProductName = value.product.name;
        },
        changeQuantity(value) {
            this.$emit("update", {
                ...this.reference,
                quantity: value,
                appliedDiscountPerReference: this.appliedDiscountPerReference,
                ...(this.enabledSalePrice ? { salePrice: this.salePrice } : {})
            });
        },
        changeAvailableDiscount(value) {
            if (!value) {
                this.appliedDiscountPerReference = 0;
                this.blurAppliedDiscountPerReference();
            }
        },
        editProductName() {
            if (this.enabled) {
                this.isEditingName = true;
            }
        },
        saveProductName() {
            if (this.editedProductName.trim() !== "") {
                this.$emit("update", {
                    ...this.reference,
                    product: {
                        ...this.reference.product,
                        name: this.editedProductName
                    }
                });
            }
            this.isEditingName = false;
        }
    }
};
</script>
